import React from 'react';
import { connect } from 'react-redux';
import classNames from 'classnames';
import _ from 'lodash';
import { Field } from 'redux-form';
import { SearchSelectBox, GroupsSelectBox, MultiTagFilteringFieldRow, OfficesSelectBox } from '../jbc-front/components/SearchForm';
import { RadioField } from 'jbc-front/components/Form';
import jbcStyles from 'jbc-front/components/SearchForm.scss';

import { convertNumberToStrOfValueInArray, convertArrayIntoJbcStyle } from 'src/utils/Utils';
import styles from 'src/components/AdditionalSearchFields.scss';
import { getSelectOptions } from 'src/reducers/selectOptions';
import { EMPLOYMENT_STATUSES, ROLL_ACCOUNT_TYPE } from 'src/constants/Options';
import { useTranslation } from 'react-i18next';

const TARGET_EMPLOYEE_BONUS = [
  { value: 'all', label: 'すべて' },
  { value: 'checked_on', label: '対象者のみ' },
  { value: 'checked_off', label: '非対象者のみ' }
];

export const groupFullName = ({ label, parentDepartmentId }, groups) =>
  parentDepartmentId && groups[parentDepartmentId]
    ? `${groupFullName(groups[parentDepartmentId], groups)}->${label}`
    : label;

const MemoColors = ({ input: inputProps, list }) => (
  <div className={styles.memoColors}>
    {list.map(item => (
      <label key={item.id} className={classNames('input-label', styles.memeLabel)}>
        <input
          type="checkbox"
          className="m-checkbox-input"
          checked={inputProps.value.indexOf(item.id) >= 0}
          onChange={() => {
            const newValues =
              inputProps.value.indexOf(item.id) >= 0
                ? inputProps.value.filter(ele => ele !== item.id)
                : [...inputProps.value, item.id];
            inputProps.onChange(newValues);
            inputProps.onBlur(newValues);
          }}
        />
        <span className={classNames('m-checkbox-parts', styles.memeCheckbox)}>
          <span style={{ color: item.name }}>●</span>
        </span>
      </label>
    ))}
  </div>
);

const AdditionalSearchFields = ({
  groups = [],
  positions = [],
  employmentTypes = [],
  occupations = [],
  payrollRuleGroups = [],
  otherSearchOptions = [],
  memoColors = [],
  offices = [],
  containsOtherSearch = true,
  containsBonusTarget,
  containAccountTypes,
  containEmployeeEnrollmentStatus
}) => {
  const { t } = useTranslation();
  return (
    <div className={jbcStyles.details}>
      <div className={styles.flex}>
        <div className={styles.left}>
          <div className={jbcStyles.detailsRow}>
            <div className={jbcStyles.detailsHeader}>{t('components.AdditionalSerchFields.office')}</div>
            <div className={jbcStyles.detailsBody}>
              <div className={jbcStyles.detailsBodyItems}>
                <Field
                  name="offices"
                  component={OfficesSelectBox}
                  options={offices.map(office => ({
                    value: `${office.value}`,
                    label: `${office.label}`,
                  }))}
                />
              </div>
            </div>
          </div>
          <div className={jbcStyles.detailsRow}>
            <div className={jbcStyles.detailsHeader}>{t('components.AdditionalSerchFields.Group')}</div>
            <div className={jbcStyles.detailsBody}>
              <div className={jbcStyles.detailsBodyItems}>
                <Field
                  name="groups"
                  component={GroupsSelectBox}
                  options={groups.map(group => ({
                    value: `${group.value}`,
                    label: groupFullName(group, _.keyBy(groups, 'value')),
                    parent_value: `${group.parentDepartmentId}`
                  }))}
                />
              </div>
            </div>
          </div>
          <MultiTagFilteringFieldRow header={t('components.AdditionalSerchFields.Position')} fieldName="positions" list={convertArrayIntoJbcStyle(positions)} />
          <MultiTagFilteringFieldRow
            header={t('components.AdditionalSerchFields.Employment_type')}
            fieldName="employmentTypes"
            list={convertArrayIntoJbcStyle(employmentTypes)}
          />
          <MultiTagFilteringFieldRow header={t('components.AdditionalSerchFields.Occupation')} fieldName="occupations" list={convertArrayIntoJbcStyle(occupations)} />
          <div className={jbcStyles.detailsRow}>
            <div className={jbcStyles.detailsHeader}>{t('features.EmployeePays.components.DownloadColumnDetail.salary_rules')}</div>
            <div className={jbcStyles.detailsBody}>
              <div className={jbcStyles.detailsBodyItems}>
                <Field
                  name="payrollRuleGroup"
                  component={SearchSelectBox}
                  options={convertNumberToStrOfValueInArray(payrollRuleGroups)}
                  width={400}
                  clearable
                  placeholder={t('components.AdditionalSerchFields.Select_salary_rules_group')}
                />
              </div>
            </div>
          </div>
          {containsOtherSearch && (
            <div className={jbcStyles.detailsRow}>
              <div className={jbcStyles.detailsHeader}>{t('components.AdditionalSerchFields.Other_conditions')}</div>
              <div className={jbcStyles.detailsBody}>
                <div className={jbcStyles.detailsBodyItems}>
                  <Field
                    name="otherSearch"
                    component={SearchSelectBox}
                    options={otherSearchOptions}
                    width={400}
                    clearable
                    placeholder={t('components.AdditionalSerchFields.Select_conditions')}
                  />
                </div>
              </div>
            </div>
          )}
          {containsBonusTarget && (
            <div className={jbcStyles.detailsRow}>
              <div className={jbcStyles.detailsHeader}>{t('components.AdditionalSerchFields.Subjects_for_bonus_payment')}</div>
              <div className={jbcStyles.detailsBody}>
                <RadioField name="bonusTarget" label="" options={TARGET_EMPLOYEE_BONUS} />
              </div>
            </div>
          )}
          {containEmployeeEnrollmentStatus && (
            <MultiTagFilteringFieldRow
              header={t('components.AdditionalSerchFields.Employment_status')}
              fieldName="enrollmentStatuses"
              list={convertArrayIntoJbcStyle(EMPLOYMENT_STATUSES)}
            />
          )}
          {containAccountTypes && (
            <MultiTagFilteringFieldRow
              header={
                <div className={styles.multiLineHeader}>
                  {t('components.AdditionalSerchFields.Employee_information')}<br />
                  {t('components.AdditionalSerchFields.Available_Not_available')}
                </div>
              }
              fieldName="accountType"
              list={convertArrayIntoJbcStyle(ROLL_ACCOUNT_TYPE)}
            />
          )}
        </div>
        <div className={styles.right}>
          <div>{t('components.AdditionalSerchFields.Note_color')}</div>
          <Field name="memoColors" component={MemoColors} list={convertArrayIntoJbcStyle(memoColors)} />
          <Field
            name="noMemo"
            component={({ input: inputProps }) => (
              <div>
                <label className="input-label">
                  <input
                    type="checkbox"
                    className="m-checkbox-input"
                    {...inputProps}
                    defaultChecked={inputProps.value === '1'}
                    onChange={() => {
                      const newValues = inputProps.value === '1' ? '0' : '1';
                      inputProps.onChange(newValues);
                      inputProps.onBlur(newValues);
                    }}
                  />
                  <span className="m-checkbox-parts">{t('components.AdditionalSerchFields.No_note')}</span>
                </label>
              </div>
            )}
          />
        </div>
      </div>
    </div>
  );
};
const mapStateToProps = state => ({
  groups: getSelectOptions(state, 'flattenGroups'),
  positions: getSelectOptions(state, 'positions'),
  employmentTypes: getSelectOptions(state, 'connectedEmploymentTypes'),
  occupations: getSelectOptions(state, 'occupations'),
  payrollRuleGroups: getSelectOptions(state, 'payrollRuleGroups'),
  otherSearchOptions: getSelectOptions(state, 'otherSearchOptions'),
  memoColors: getSelectOptions(state, 'memoColors'),
  offices: getSelectOptions(state, 'offices')
});
export default connect(mapStateToProps)(AdditionalSearchFields);
